import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import { Box, Button, Container } from "@mui/material";
import { callMsGraph } from "api/graph";
import CryptoJS from "crypto-js";
import { useLazyGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import {
  useLazyGetUserQuery,
  useLazyGetUserWithOrgMembershipStatusesQuery,
} from "state/rtkQuery/user/userQueries";
import { setAuthState } from "state/slices/auth/authSlice";
import ExtractUserPermissions from "utils/FetchUserPermissions";
import { getMSALToken } from "utils/getMSALToken";

import ProgressBar from "./ProgressBar/ProgressBar";

import { loginRequest } from "../../authConfig/authConfig";

import "./home.css";

function Home() {
  const { instance, inProgress, accounts } = useMsal();
  const auth = useIsAuthenticated();
  const location = useLocation();
  const [from, setFrom] = useState("/dashboard");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [loader, setLoader] = useState(false);
  // Some browser extensions (ad/privacy blockers) block same-origin media
  // requests based on generic keyword filters — this has nothing to do with
  // the request actually failing, so fall back to the poster still frame as
  // a plain image rather than leaving an empty/broken-looking video element.
  const [showcaseMediaFailed, setShowcaseMediaFailed] = useState(false);

  useEffect(() => {
    if (location?.state?.from?.pathname) {
      setFrom(location.state?.from?.pathname);
      if (location?.state?.from?.search) {
        setFrom(from + location?.state?.from?.search);
      }
    }
  }, [location]);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, [auth, inProgress, instance]);

  // RTK Query for getting userRecord from db
  const [fetchPlatformData] = useLazyGetPlatformsQuery();
  const [fetchUserData] = useLazyGetUserQuery();
  const [fetchUserDataWithOrgStatus] =
    useLazyGetUserWithOrgMembershipStatusesQuery();

  const [accountState, setAccountState] = useState(accounts?.[0]);

  // loginRedirect navigates the whole window away, so this promise never
  // resolves in this page load. Once Azure AD redirects back, MsalProvider
  // processes the response and populates `accounts` — the effect below syncs
  // accountState from that instead of from a resolved login promise.
  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((e) => {
      setLoader(false);
      console.log(e);
    });
  };

  // Sync accountState once MSAL populates accounts after the redirect back
  // from Azure AD (accountState is otherwise only seeded once, on first render).
  useEffect(() => {
    if (!accountState && accounts?.[0]) {
      setAccountState(accounts[0]);
    }
  }, [accounts, accountState]);

  const isAuthed = useIsAuthenticated();
  const authState = useSelector((state) => state.auth);
  const [tmp, setTmp] = useState();

  // Logic to bypass login page if user is already SSO'ed
  useEffect(() => {
    // Function to get auth state once user has SSO'ed
    const getAuthState = async () => {
      setLoader(true);

      // Init authState
      let authStateUpdated = {
        authToken: null,
        userAccount: null,
        permissions: [],
      };

      // Fetch id token and access token from Azure AD
      const accessTokenRes = await getMSALToken(instance, accountState, setTmp);
      authStateUpdated.authToken = accessTokenRes?.idToken;
      authStateUpdated.accessToken = accessTokenRes?.accessToken;

      authStateUpdated.userAccount = accountState;

      // Fetch user account info from MS Graph API
      const msGraphRes = await callMsGraph(
        authStateUpdated?.accessToken,
        accountState?.username
      );
      authStateUpdated.userAccount.givenName = msGraphRes?.givenName;
      authStateUpdated.userAccount.surname = msGraphRes?.surname;

      // Set updated auth state with id token, access token, and user account info in redux store
      await dispatch(setAuthState(authStateUpdated));

      // Fetch platform data to get user data with org statuses from specific platform (only GitHub for now)
      const platformRes = await fetchPlatformData();
      // Fetch user data from DB via user service API
      const userRes = await fetchUserData({
        email: accountState.username,
        platformId: platformRes.data?._id,
      });
      authStateUpdated = JSON.parse(JSON.stringify(authStateUpdated));

      // Fetch user data with org statuses if user exists in DB and platform data call is successful
      if (
        userRes.isSuccess &&
        userRes.data.length !== 0 &&
        platformRes.isSuccess
      ) {
        const userOrgRes = await fetchUserDataWithOrgStatus({
          userId: userRes?.data?.[0]?._id,
          platformId: platformRes.data?._id,
        });

        // Extract user permissions for each org from user org statuses data and store in authState
        if (userOrgRes.isSuccess) {
          authStateUpdated.permissions = await ExtractUserPermissions([
            ...userOrgRes?.data[0],
            ...userOrgRes?.data[1],
          ]);
        }
      }

      // Encrypt and store authState in local storage to rehydrate state on refresh
      localStorage.setItem(
        "authStateEncrypted",
        CryptoJS.AES.encrypt(
          JSON.stringify(authStateUpdated),
          "someKey"
        ).toString()
      );

      await dispatch(setAuthState(authStateUpdated));

      setLoader(false);
      navigate(from, { replace: true });
    };

    // If user is authenticated and accountState is not null, get auth state otherwise redirect to page from which user was redirected to login
    if (isAuthed && accountState && !authState.authToken) {
      getAuthState();
    } else if (isAuthed && accountState && authState.authToken) {
      navigate(from, { replace: true });
    }
  }, [isAuthed, accountState]);

  return loader ? (
    <Container>
      <Box
        sx={{
          height: 420,
          marginTop: "4rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          margin: "auto",
        }}
      >
        <ProgressBar />
        <p style={{ marginTop: "1rem" }}>Fetching user data...</p>
      </Box>
    </Container>
  ) : (
    <div>
      <UnauthenticatedTemplate>
        <div className="login-page-container">
          {/* ── Left Panel ── */}
          <div className="login-left-panel">
            <div className="login-left-content">
              {/* Deloitte logo */}
              <img
                src="/deloitte-full-logo.svg"
                alt="Deloitte"
                className="deloitte-logo"
              />

              {/* ECR Portal heading */}
              <h1 className="ecr-title">ECR Portal</h1>

              {/* Subtitle */}
              <p className="ecr-subtitle">
                Welcome to the DT-Global Enterprise Code Repository (ECR)
              </p>

              {/* GitHub brand */}
              <div className="github-brand">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 98 96"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-label="GitHub"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z"
                    fill="#1a1a1a"
                  />
                </svg>
                <span className="github-brand-text">GitHub</span>
              </div>

              {/* Description */}
              <p className="ecr-description">
                Request access to GitHub organizations, join teams, and
                collaborate across Deloitte - all in one place.
              </p>

              {/* Feature checklist */}
              <ul className="feature-list">
                {[
                  "Browse & Discover GitHub Organisations",
                  "One-Click Access Request",
                  "Real-Time Request Tracking",
                ].map((item) => (
                  <li key={item}>
                    <svg
                      width="10"
                      height="7"
                      viewBox="0 0 10 7"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M0.75 3.75L3.25 6.25L8.75 0.75"
                        stroke="#86BC25"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>

              {/* Sign In button */}
              <Button
                className="sign-in-btn-new"
                onClick={handleLogin}
              >
                {/* Microsoft Windows logo */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 21 21"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                Sign In
              </Button>
            </div>
          </div>

          {/* ── Right Panel – Showcase Media ── */}
          <div className="login-right-panel">
            {showcaseMediaFailed ? (
              <img
                src="/shark-swim.png"
                alt=""
                className="login-showcase-media"
              />
            ) : (
              <video
                className="login-showcase-media"
                poster="/shark-swim.png"
                autoPlay
                muted
                loop
                playsInline
                onError={() => setShowcaseMediaFailed(true)}
              >
                <source src="/media/ecr-portal-intro.mp4" type="video/mp4" />
              </video>
            )}
          </div>
        </div>
      </UnauthenticatedTemplate>
    </div>
  );
}

export default Home;
